import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { ErrorBoundary } from '@hotjar/error-handler';
import { RELEVANT_ACTION_NOTIFICATION_WIDTH_IN_PX } from '@hotjar/webapp/features/Playbacks//RelevantActionNotification';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { Lollipops } from '@hotjar/webapp/features/Playbacks/Timeline/Lollipops/Lollipops';
import type {
  TimelineActiveItem,
  TimelineNewItemMetadata,
} from '@hotjar/webapp/features/Playbacks/types';
import { useCurrentSiteId } from '@hotjar/site';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useOrganization } from '@hotjar/organization';
import { CommentsContext } from '@hotjar/webapp/contexts/comments';
import type { RecordingHighlight } from '@hotjar/webapp/domains/highlights/recording/model';

import { ExistingHighlightPopover } from '../ExistingHighlightPopover';
import { getHighlightBoundaries } from './get-highlight-boundaries';
import { NewHighlightFrame } from './NewHighlightFrame';
import { NewHighlightPopover } from './NewHighlightPopover';
import { useRecordingHighlights } from './use-recording-highlights';
import { validateNewHighlightTime } from './utils';

const HIGHLIGHT_TIME_START_OFFSET = 10_000;
const HIGHLIGHT_TIME_END_OFFSET = 20_000;

type HighlightTime = {
  end: number;
  marker: number;
  start: number;
};

function getHighlightTime(
  newHighlightTime: number | null,
  recordingDuration: number,
): HighlightTime | null {
  if (newHighlightTime === null) {
    return null;
  }

  return {
    end: Math.min(recordingDuration, newHighlightTime + HIGHLIGHT_TIME_END_OFFSET),
    marker: newHighlightTime,
    start: Math.max(0, newHighlightTime - HIGHLIGHT_TIME_START_OFFSET),
  };
}

const StyledNewHighlightFrame = styled(NewHighlightFrame)`
  left: 0;
  position: absolute;
  right: 0;
  // NOTE: +9px is to exactly wrap the current position marker
  top: calc(50% + 9px);
`;

export interface HighlightsTimelineProps {
  activeItemId: number | null;
  apiToken: string | null;
  currentUser: React.ComponentProps<typeof ExistingHighlightPopover>['currentUser'] & {
    id: Parameters<typeof useOrganization>[1];
  };
  duration: number;
  isPlaying: boolean;
  newHighlightMetadata: TimelineNewItemMetadata;
  onCommentsFetched: () => void;
  onNewHighlightTimeChange: (newTime: number | null) => void;
  onPause: (showAnimation?: boolean, isManual?: boolean) => void;
  onShare: React.ComponentProps<typeof ExistingHighlightPopover>['onShare'];
  organizationId: number;
  recordingId: number;
  resetActiveItem: () => void;
  setActiveItem: (item: TimelineActiveItem | null) => void;
  timelineBox: React.ComponentProps<typeof Lollipops>['timelineBox'];
  jumpTo: (targetTime: number) => void;
  onPlay: (showAnimation?: boolean) => void;
  pause: (showAnimation?: boolean, isManual?: boolean) => void;
  loaded: boolean;
}

export const HighlightsTimeline = ({
  activeItemId,
  apiToken = null,
  currentUser,
  duration,
  isPlaying,
  newHighlightMetadata,
  onNewHighlightTimeChange,
  onPause,
  onShare,
  organizationId,
  recordingId,
  resetActiveItem,
  setActiveItem,
  timelineBox,
  jumpTo,
  onPlay,
  pause,
  loaded,
  onCommentsFetched,
}: HighlightsTimelineProps): JSX.Element | null => {
  const setIsRelevantActionOverlappingHighlight = useRealTimeStore(
    (state) => state.setIsRelevantActionOverlappingHighlight,
  );
  const currentTime = useRealTimeStore((state) => state.currentTime);
  const [newHighlightTime, setNewHighlightTime] = useState<HighlightTime | null>(
    getHighlightTime(newHighlightMetadata.time, duration),
  );
  const [newHighlightTimeError, setNewHighlightTimeError] =
    useState<ReturnType<typeof validateNewHighlightTime>>(null);
  const currentSiteId = useCurrentSiteId();
  const { data, isFetched, refetch } = useRecordingHighlights(currentSiteId, recordingId);
  const { data: currentOrganization } = useOrganization(organizationId, currentUser.id);
  const { success } = useNotify();

  const newHighlightPosition = getHighlightBoundaries({
    highlightTime: newHighlightTime,
    recordingDuration: duration,
  });

  const newHighlight = useMemo(() => {
    if (newHighlightTime === null || newHighlightPosition === null) {
      return null;
    }
    return {
      time: newHighlightTime,
      position: newHighlightPosition,
      type: newHighlightMetadata.type,
    };
  }, [newHighlightTime, newHighlightPosition, newHighlightMetadata.type]);

  const relevantActionOffset = useMemo((): number => {
    if (!timelineBox) {
      return 0;
    }

    // The relevant actions menu is displayed in the middle of the current progress handle.
    // So half to the left and half to the right of the progress handle.
    // For this reason, we take into consideration only half of the total length.
    // e.g. x = RelevantAction's width
    //             |x/2 + x/2|  Highlight
    // Timeline -------🔴-----------💡--------
    //           ProgressHandle
    const relevantActionWidth = RELEVANT_ACTION_NOTIFICATION_WIDTH_IN_PX / 2;

    return Math.ceil((relevantActionWidth * duration) / timelineBox.width);
  }, [timelineBox, duration]);

  // We detect a collision between RelevantActionNotification and Highlight
  // checking if the currentTime is colliding the highlight's sticker label.
  useEffect(() => {
    if (!data) {
      setIsRelevantActionOverlappingHighlight(false);
      return;
    }

    const isOverlapping = data.items.some((highlight) => {
      const highlightTime = getHighlightTime(highlight.metadata.clip.marker ?? null, duration);

      if (!highlightTime) {
        return false;
      }

      const startTimeWithOffset =
        highlightTime.start > relevantActionOffset
          ? highlightTime.start - relevantActionOffset
          : highlightTime.start;
      const endTimeWithOffset =
        highlightTime.end + relevantActionOffset < duration
          ? highlightTime.end + relevantActionOffset
          : highlightTime.end;

      return currentTime >= startTimeWithOffset && currentTime <= endTimeWithOffset;
    });

    setIsRelevantActionOverlappingHighlight(isOverlapping);
  }, [data, currentTime, relevantActionOffset, duration, setIsRelevantActionOverlappingHighlight]);

  useEffect(() => {
    setNewHighlightTime(getHighlightTime(newHighlightMetadata.time, duration));
  }, [newHighlightMetadata.time, duration]);

  useEffect(() => {
    if (isPlaying) {
      resetActiveItem();
    }
  }, [isPlaying, resetActiveItem]);

  useEffect(() => {
    setNewHighlightTime(getHighlightTime(newHighlightMetadata.time, duration));
  }, [duration, newHighlightMetadata.time]);

  useEffect(() => {
    if (newHighlightMetadata.time !== null) {
      resetActiveItem();
    }
  }, [newHighlightMetadata, resetActiveItem]);

  useEffect(() => {
    if (newHighlightTimeError === null) {
      return undefined;
    }

    const resetError = setTimeout(() => {
      setNewHighlightTimeError(null);
    }, 3000);

    return () => clearTimeout(resetError);
  }, [newHighlightTimeError]);

  const handleNewHighlightTimeChange = useCallback(
    (updatedNewHighlightTime) => {
      const error = validateNewHighlightTime({
        newHighlightTime: updatedNewHighlightTime,
      });

      setNewHighlightTimeError(error);

      if (error) {
        return;
      }

      setNewHighlightTime(updatedNewHighlightTime);
    },
    [setNewHighlightTime, setNewHighlightTimeError],
  );

  const handlePositionChange = useCallback(
    (updatedNewHighlightPosition) => {
      if (newHighlightTime === null) {
        return;
      }

      handleNewHighlightTimeChange({
        end: (updatedNewHighlightPosition.end / 100) * duration,
        marker: newHighlightTime.marker,
        start: (updatedNewHighlightPosition.start / 100) * duration,
      });
    },
    [duration, newHighlightTime, handleNewHighlightTimeChange],
  );

  const [onCommentsFetchedSent, setCommentsFetchedSent] = useState(false);

  useEffect(() => {
    if (!isFetched || onCommentsFetchedSent || !data) {
      return;
    }
    setCommentsFetchedSent(true);
    onCommentsFetched();
  }, [isFetched, data, onCommentsFetchedSent, onCommentsFetched, setCommentsFetchedSent]);

  if (!data?.items) return null;

  return (
    <ErrorBoundary squadSentryTag="workflow">
      <Lollipops
        activeItemId={activeItemId}
        items={data.items.map((item) => {
          const position =
            getHighlightBoundaries({
              highlightTime: getHighlightTime(item.metadata.clip.marker, duration),
              recordingDuration: duration,
            })?.marker ?? 0;

          return {
            ...item,
            id: Number(item.id),
            position,
            type: 'recording',
          };
        })}
        newItem={newHighlight}
        renderExistingContent={(
          item,
          {
            deactivateCurrentItem,
            deactivateNotInteractedCurrentItem,
            markCurrentItemAsInteracted,
          },
        ) => (
          <CommentsContext>
            <ExistingHighlightPopover
              isDesktop
              apiToken={apiToken}
              currentUser={currentUser}
              deactivateCurrentItem={deactivateCurrentItem}
              deactivateNotInteractedCurrentItem={deactivateNotInteractedCurrentItem}
              isPlaying={isPlaying}
              item={item as unknown as RecordingHighlight /* quick fix for now */}
              markCurrentItemAsInteracted={markCurrentItemAsInteracted}
              onPause={onPause}
              onShare={onShare}
              organizationId={organizationId}
              refetchHighlights={refetch}
              setActiveItem={setActiveItem}
              siteId={currentSiteId}
            />
          </CommentsContext>
        )}
        renderNewContent={() => (
          <NewHighlightPopover
            currentOrganization={currentOrganization}
            deactivateCurrentItem={() => onNewHighlightTimeChange(null)}
            newHighlightTime={newHighlightTime}
            newHighlightTimeError={newHighlightTimeError}
            onNewHighlightTime={handleNewHighlightTimeChange}
            recordingId={recordingId}
            setActiveItem={setActiveItem}
            showSuccessMessage={success}
            siteId={currentSiteId}
            jumpTo={jumpTo}
            onPlay={onPlay}
            pause={pause}
            loaded={loaded}
            isPlaying={isPlaying}
          />
        )}
        setActiveItem={setActiveItem}
        showLollipopFooter
        showStickerLabels
        timelineBox={timelineBox}
      />
      {newHighlight ? (
        <StyledNewHighlightFrame
          onPositionChange={handlePositionChange}
          position={newHighlight.position}
        />
      ) : null}
    </ErrorBoundary>
  );
};
